import {
  Component,
  ComponentType,
  type ErrorInfo,
  PropsWithChildren,
} from 'react';

import { Fallback, Props as FallbackProps } from './Fallback';
import { SDKInteropError } from '../../errors';

export type Props = PropsWithChildren<{
  fallback?: ComponentType<FallbackProps>;
}>;

type State = { error: unknown };

export class ErrorBoundary extends Component<Props, State> {
  static readonly defaultProps = {
    fallback: Fallback,
  };

  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: unknown) {
    // Temporarily catch all errors to determine handling
    return { error };
  }

  componentDidCatch(error: unknown, { componentStack: _ }: ErrorInfo) {
    if (error instanceof SDKInteropError) {
      // error is set in getDerivedStateFromError, no action needed
      // extra logging can go here if needed
    } else {
      // Reset state and re-throw non-SDK errors to bubble up
      this.setState({ error: null });
      throw error;
    }
  }

  render() {
    if (this.state.error && this.props.fallback) {
      const FallbackComponent = this.props.fallback;
      return <FallbackComponent error={this.state.error} />;
    }

    return this.props.children;
  }
}
